import { ArrowRight, Play } from "lucide-react";
import { useLeadModal } from "../../context/LeadModalContext";

const HERO_IMAGE = "/hero-poster-new.png";

const outcomes = [
    { value: "4 days", label: "Avg. time to first agent" },
    { value: "62%", label: "Avg. ticket deflection" },
    { value: "180+", label: "Hours saved per month" },
    { value: "9", label: "Paying customers on ezScribe" },
];

export default function Hero() {
    const { openModal } = useLeadModal();

    return (
        <section
            data-testid="hero-section"
            className="relative pt-32 sm:pt-36 pb-20 sm:pb-24 overflow-hidden"
        >
            {/* layered background — single restrained glow */}
            <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
            <div className="absolute inset-0 radial-glow opacity-70 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    {/* Left: copy */}
                    <div className="lg:col-span-7 text-left">
                        <div
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/10 mb-7 animate-fade-up"
                            data-testid="hero-badge"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]" />
                            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#94a3b8]">
                                AI agents shipped in days · Not months
                            </span>
                        </div>

                        <h1
                            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.95] animate-fade-up"
                            style={{ animationDelay: "80ms" }}
                        >
                            Custom AI agents.{" "}
                            <span className="text-gradient-cyan">Built ez.</span>{" "}
                            <span className="text-white/55">Deployed faster.</span>
                        </h1>

                        <p
                            className="mt-7 max-w-xl text-lg sm:text-xl text-[#94a3b8] leading-relaxed animate-fade-up"
                            style={{ animationDelay: "160ms" }}
                        >
                            We build the AI agents, web apps, and landing pages your business
                            actually needs — without the agency markup, the bloated timelines,
                            or the &ldquo;it&apos;ll be ready next quarter&rdquo; excuses.
                        </p>

                        <div
                            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-up"
                            style={{ animationDelay: "240ms" }}
                        >
                            <button
                                onClick={() => openModal("hero-primary")}
                                data-testid="hero-trial-btn"
                                className="group inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold hover:bg-white transition-colors duration-300"
                            >
                                Book a Free Call
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <a
                                href="#showcase"
                                data-testid="hero-secondary-btn"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-lg border border-white/15 text-white hover:border-[#00f0ff] hover:text-[#00f0ff] transition-colors duration-300"
                            >
                                <Play className="w-4 h-4" />
                                See What We Build
                            </a>
                        </div>

                        <div
                            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#64748b] animate-fade-up"
                            style={{ animationDelay: "320ms" }}
                        >
                            <span className="inline-flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]" />
                                No commitment to start
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]" />
                                Cancel any month
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]" />
                                Custom plan in 24h
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-[#00f0ff]">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]" />
                                Built by the founder of ezScribe
                            </span>
                        </div>
                    </div>

                    {/* Right: hero poster */}
                    <div
                        className="lg:col-span-5 relative animate-fade-up"
                        style={{ animationDelay: "360ms" }}
                        data-testid="hero-mascot"
                    >
                        <div className="relative max-w-lg mx-auto">
                            {/* one soft ambient glow */}
                            <div className="absolute -inset-4 rounded-[28px] bg-[#00f0ff]/[0.08] blur-3xl pointer-events-none" />
                            <div className="relative rounded-2xl overflow-hidden border border-white/10">
                                <img
                                    src={HERO_IMAGE}
                                    alt="AI automation saving 70% in admin time and operations"
                                    className="relative z-10 w-full h-auto block"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Outcome bar — real results, no fake terminal */}
                <div
                    className="relative mt-20 max-w-5xl mx-auto animate-fade-up"
                    style={{ animationDelay: "440ms" }}
                    data-testid="hero-outcomes"
                >
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                        {outcomes.map((o) => (
                            <div
                                key={o.label}
                                data-testid={`hero-outcome-${o.value}`}
                                className="bg-[#0a1325] px-6 py-8 text-center"
                            >
                                <div className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-white">
                                    {o.value}
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.15em] text-[#64748b] mt-2 leading-relaxed">
                                    {o.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
